import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  input,
  output,
  Renderer2,
  signal,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { Menu, MenuModule } from 'primeng/menu';
import { filter, tap } from 'rxjs';
import { MenuTool } from '../../../interfaces/MenuTool.interface';
import { NavigationItem } from '../../../interfaces/navigationList.interface';
import {
  HomeButtonConfig,
  Profile,
} from '../../../interfaces/PageContainerConfiguration.interface';
import { PageContainerService } from '../../../services/page-container.service';
import { AuthenticationService } from '../../../../../auth/services/authentication.service';
import { AboutModalService } from '../../../services/aboutModal.service';

@Component({
    selector: 'st-mobile-main-sections-left-menu',
    imports: [CommonModule, MenuModule],
    templateUrl: './mobile-main-sections-left-menu.component.html',
    styleUrls: ['./mobile-main-sections-left-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MobileMainSectionsLeftMenuComponent implements AfterViewInit {
  navigationList = input.required<NavigationItem[]>();
  homeButton = input<HomeButtonConfig>();
  profile = input.required<Profile>();
  settingURL = input<string>();
  tools = input.required<MenuTool[]>();
  public isAboutModalVisible = signal<boolean>(false);
  dropDownItems: MenuItem[] = [];
  public userName = signal<string>('');
  public userEmail = signal<string>('');

  constructor(
    readonly cd: ChangeDetectorRef,
    readonly pageContainerService: PageContainerService,
    readonly router: Router,
    private readonly _renderer: Renderer2,
    private readonly _router: Router,
    public authenticationService: AuthenticationService,
    private readonly aboutModalService: AboutModalService,
    private readonly _cd: ChangeDetectorRef,
  ) {
    this.setUserDetailsFromLocalStorage();
  }

  private setUserDetailsFromLocalStorage(): void {
    const currentUser = localStorage.getItem('currentUser');
    if (currentUser) {
      const user = JSON.parse(currentUser);

      // Get user name from firstName and lastName
      const userName =
        (user?.firstName && user?.lastName
          ? `${user.firstName} ${user.lastName}`
          : null) ??
        user?.firstName ??
        'Unknown User';

      // Get user email
      const userEmail = user?.email ?? 'Unknown Email';

      this.userName.set(userName);
      this.userEmail.set(userEmail);

      this.dropDownItems = [
        {
          items: [
            {
              label: 'About Adroit',
              icon: 'icon-info',
              shortcut: '⌘+a',
              disabled: false,
              command: () => this.aboutModalService.openAboutModal(),
            },
            {
              label: 'Sign Out',
              icon: 'icon-sign-out',
              shortcut: '⌘+d',
              disabled: false,
              styleClass: 'danger',
              command: () => this.signOut(),
            },
          ],
        },
      ];
    }
  }
  sectionSelected = output<NavigationItem>();
  toolsOpen = output<void>();

  isStaticButtonActive = signal<'home' | 'profile' | 'sitting' | ''>('');

  routerEvents$ = this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    tap(event => {
      const url = event.urlAfterRedirects;
      this.checkStaticButtons(url);
      this.pageContainerService.expandMenuDrawer$.next(false);
    }),
  );

  activeSectionRoute$ = this.pageContainerService.activeSectionRoute;

  ngAfterViewInit() {
    this.checkStaticButtons(this.router.url);
  }

  selectSection(item: NavigationItem) {
    if (item.children.length) {
      this.sectionSelected.emit(item);
    } else {
      this.pageContainerService.activeSectionRoute.next(item);
      this.router.navigate([item.routeUrl]);
    }
  }

  openTools() {
    this.toolsOpen.emit();
  }

  goToHome(url: string) {
    this.pageContainerService.activeSectionRoute.next(null);
    this.router.navigate([url]);
  }

  navigateToProfile() {
    const url = this.profile()?.profileRoute;
    if (url?.length) this.router.navigate([url]);
  }

  navigateToSetting() {
    const url = this.settingURL?.();
    if (url?.length) {
      this.router.navigate([url]);
    }
  }

  checkStaticButtons(url: string) {
    const homeButton = this.homeButton();
    if (url === homeButton?.routeUrl) {
      this.isStaticButtonActive.set('home');
    } else if (url === this.profile().profileRoute) {
      this.isStaticButtonActive.set('profile');
    } else if (url === this.settingURL()) {
      this.isStaticButtonActive.set('sitting');
    } else {
      this.isStaticButtonActive.set('');
    }
  }

  public toggleMenu(event: Event, menu: Menu) {
    menu.toggle(event);

    this._renderer.setStyle(
      menu.el.nativeElement,
      'top',
      `${(event as any).y}px !important`,
    );
    this._renderer.setStyle(
      menu.el.nativeElement,
      'left',
      `${(event as any).x}px !important`,
    );
  }

  public signOut() {
    this.authenticationService.removeUserData();
    this._router.navigate(['/login']);
  }
}
